import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  useParams
} from 'react-router-dom'
import {
  getFlightOptionsApiV1FlightsOptionsTokenGet,
  selectFlightOptionApiV1FlightsOptionsTokenSelectPost
} from '../client'
import type {
  FlightOptionSetResponse,
  FlightPageOption,
  FlightPageSlice
} from '../client'
import {
  FlightEvents,
  trackEvent
} from '../analytics/posthog'
import {
  API_BASE_URL
} from '../config'
import {
  getApiErrorStatus
} from '../services/api'
import {
  CatchLogo
} from './CatchLogo'
import {
  CookieConsentBanner
} from './CookieConsentBanner'
import {
  compareKnownFirst,
  firstDepartureMs,
  formatMinutes,
  isDirect,
  sliceMinutes,
  sliceStops,
  tripMinutes
} from '../utils/flightOptions'
import {
  optionSetLoadFailure
} from '../utils/optionSetLoad'
import type {
  OptionSetLoadFailure
} from '../utils/optionSetLoad'
import {
  OptionSetUnavailable
} from './OptionSetUnavailable'

type LoadState = 'loading' | OptionSetLoadFailure | 'ready'
type SortKey = 'best' | 'cheapest' | 'fastest' | 'earliest'

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(price)
  } catch {
    return `${currency} ${Math.round(price)}`
  }
}

// Duffel segment times are local ISO strings without an offset; render the
// time-of-day (and date) as-is rather than shifting to the viewer's zone.
function formatTime(iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  }).format(date)
}

function stopsLabel(slice: FlightPageSlice): string {
  const stops = sliceStops(slice)
  if (stops === 0) {
    return 'Direct'
  }
  const vias = (slice.segments ?? [])
    .slice(0, -1)
    .map((seg) => seg.destination)
    .filter(Boolean)
    .join(', ')
  return `${stops} stop${stops > 1 ? 's' : ''}${vias ? ` (${vias})` : ''}`
}

// Duffel publishes per-airline logos at a stable, IATA-keyed CDN path.
function airlineLogoUrl(iata: string | null | undefined): string | null {
  if (!iata) {
    return null
  }
  return `https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${iata}.svg`
}

function AirlineLogo({
  iata,
  name
}: {
  iata: string | null | undefined
  name: string | null | undefined
}): JSX.Element {
  const [failed, setFailed] = useState(false)
  const url = airlineLogoUrl(iata)
  if (!url || failed) {
    return (
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-catch-grey text-xs font-medium text-gray-500">
        {(iata ?? name ?? '?').slice(0, 2).toUpperCase()}
      </div>
    )
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-catch-beige-dark bg-white">
      <img
        src={url}
        alt={name ?? ''}
        loading="lazy"
        className="h-7 w-7 object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  )
}

function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-catch-white text-catch-black font-sans">
      <header className="sticky top-0 z-10 border-b border-catch-beige-dark bg-catch-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center px-5 py-3">
          <CatchLogo size="sm" />
        </div>
      </header>
      {children}
      <CookieConsentBanner storageKey="flights_cookie_consent" />
    </div>
  )
}

export function FlightOptionsPage(): JSX.Element {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<LoadState>('loading')
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [data, setData] = useState<FlightOptionSetResponse | null>(null)
  const [selectingOfferId, setSelectingOfferId] = useState<string | null>(null)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const [alreadyTaken, setAlreadyTaken] = useState(false)
  const [sort, setSort] = useState<SortKey>('best')
  const [directOnly, setDirectOnly] = useState(false)
  const [refundableOnly, setRefundableOnly] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load(): Promise<void> {
      if (!token) {
        setState('expired')
        return
      }
      setState('loading')
      const res = await getFlightOptionsApiV1FlightsOptionsTokenGet({
        path: { token },
        baseURL: API_BASE_URL,
        throwOnError: false
      })
      if (cancelled) {
        return
      }
      if (res.error || !res.data) {
        const status = getApiErrorStatus(res)
        const failure = optionSetLoadFailure(status)
        setState(failure)
        trackEvent(FlightEvents.OPTIONS_UNAVAILABLE, {
          token, failure, status
        })
        return
      }
      const result = res.data
      setData(result)
      setAlreadyTaken(result.already_selected ?? false)
      setState('ready')
      trackEvent(FlightEvents.OPTIONS_VIEWED, {
        token,
        options: result.options.length
      })
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token, loadAttempt])

  const retryLoad = useCallback(() => {
    setLoadAttempt((attempt) => attempt + 1)
  }, [])

  const handleSelect = useCallback(
    async (offerId: string): Promise<void> => {
      if (!token || selectingOfferId || selectedLabel || alreadyTaken) {
        return
      }
      setSelectingOfferId(offerId)
      const res = await selectFlightOptionApiV1FlightsOptionsTokenSelectPost({
        path: { token },
        body: { offer_id: offerId },
        baseURL: API_BASE_URL,
        throwOnError: false
      })
      setSelectingOfferId(null)
      if (res.error) {
        // A 409 means someone already picked from this set (or a double-tap);
        // other errors are transient — leave the list so the user can retry.
        if (res.response?.status === 409) {
          setAlreadyTaken(true)
          trackEvent(FlightEvents.OPTION_ALREADY_TAKEN, { offer_id: offerId })
        }
        return
      }
      setSelectedLabel(res.data?.flight_label ?? 'your pick')
      trackEvent(FlightEvents.OPTION_CHOSEN, {
        offer_id: offerId,
        flight_label: res.data?.flight_label
      })
    },
    [token, selectingOfferId, selectedLabel, alreadyTaken]
  )

  // "Best for" highlights are computed across the full set (not the filtered
  // view) so the badges are stable as the user toggles filters.
  const { cheapestId, fastestId, cheapestPrice, recommendedId } = useMemo(() => {
    const opts = data?.options ?? []
    let cheap: FlightPageOption | null = null
    let fast: FlightPageOption | null = null
    let fastestMinutes: number | null = null
    for (const o of opts) {
      if (!cheap || o.total_amount < cheap.total_amount) {
        cheap = o
      }
      const minutes = tripMinutes(o)
      if (minutes !== null && (fastestMinutes === null || minutes < fastestMinutes)) {
        fast = o
        fastestMinutes = minutes
      }
    }
    return {
      cheapestId: cheap?.offer_id ?? null,
      fastestId: fast?.offer_id ?? null,
      cheapestPrice: cheap?.total_amount ?? 0,
      recommendedId: opts[0]?.offer_id ?? null
    }
  }, [data])

  const visible = useMemo(() => {
    let opts = [...(data?.options ?? [])]
    if (directOnly) {
      opts = opts.filter(isDirect)
    }
    if (refundableOnly) {
      opts = opts.filter((o) => o.refundable)
    }
    if (sort === 'cheapest') {
      opts.sort((a, b) => a.total_amount - b.total_amount)
    } else if (sort === 'fastest') {
      opts.sort((a, b) => compareKnownFirst(tripMinutes(a), tripMinutes(b)))
    } else if (sort === 'earliest') {
      opts.sort((a, b) => compareKnownFirst(firstDepartureMs(a), firstDepartureMs(b)))
    }
    return opts
  }, [data, sort, directOnly, refundableOnly])

  const maxTripMinutes = useMemo(
    () => Math.max(1, ...(data?.options ?? []).map((o) => tripMinutes(o) ?? 0)),
    [data]
  )

  if (state === 'loading') {
    return (
      <PageShell>
        <div className="flex min-h-[60vh] items-center justify-center text-gray-500">
          Loading your flights…
        </div>
      </PageShell>
    )
  }

  if (state === 'expired' || state === 'unreachable' || !data) {
    return (
      <PageShell>
        <OptionSetUnavailable
          failure={state === 'unreachable' ? 'unreachable' : 'expired'}
          onRetry={retryLoad}
          className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 text-center"
        />
      </PageShell>
    )
  }

  if (selectedLabel) {
    return (
      <PageShell>
        <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 text-center animate-fade-in">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-3xl text-green-700">
            ✓
          </div>
          <h1 className="mt-4 font-display text-3xl">Nice pick</h1>
          <p className="mt-3 text-gray-600">
            I&apos;ve let your assistant know you like the{' '}
            <strong>{selectedLabel}</strong> option. Head back to your chat and
            it&apos;ll take it from there.
          </p>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 pb-20 sm:px-5">
        <div className="pb-3 pt-5">
          <h1 className="font-display text-3xl sm:text-4xl">Flight options</h1>
          {data.search_summary && (
            <p className="mt-1 text-sm text-gray-600">{data.search_summary}</p>
          )}
          {alreadyTaken && (
            <p className="mt-2 text-sm text-catch-red">
              You&apos;ve already picked one of these — check your chat to keep going.
            </p>
          )}
        </div>

        <FilterBar
          sort={sort}
          onSort={setSort}
          directOnly={directOnly}
          onDirect={() => setDirectOnly((v) => !v)}
          refundableOnly={refundableOnly}
          onRefundable={() => setRefundableOnly((v) => !v)}
          count={visible.length}
        />

        <ul className="mt-4 space-y-3">
          {visible.map((option) => (
            <li key={option.offer_id}>
              <FlightCard
                option={option}
                maxTripMinutes={maxTripMinutes}
                cheapestPrice={cheapestPrice}
                isCheapest={option.offer_id === cheapestId}
                isFastest={option.offer_id === fastestId}
                isRecommended={option.offer_id === recommendedId}
                selecting={selectingOfferId === option.offer_id}
                disabled={alreadyTaken || selectingOfferId !== null}
                onChoose={() => void handleSelect(option.offer_id)}
              />
            </li>
          ))}
          {visible.length === 0 && (
            <li className="rounded-xl border border-dashed border-catch-beige-dark px-4 py-8 text-center text-sm text-gray-500">
              No flights match these filters.
            </li>
          )}
        </ul>

        <p className="mt-6 text-center text-xs text-gray-400">
          Powered by Catch · prices are indicative and confirmed at booking
        </p>
      </div>
    </PageShell>
  )
}

function FilterBar({
  sort,
  onSort,
  directOnly,
  onDirect,
  refundableOnly,
  onRefundable,
  count
}: {
  sort: SortKey
  onSort: (s: SortKey) => void
  directOnly: boolean
  onDirect: () => void
  refundableOnly: boolean
  onRefundable: () => void
  count: number
}): JSX.Element {
  const sorts: { key: SortKey, label: string }[] = [
    { key: 'best', label: 'Best' },
    { key: 'cheapest', label: 'Cheapest' },
    { key: 'fastest', label: 'Fastest' },
    { key: 'earliest', label: 'Earliest' }
  ]
  return (
    <div className="sticky top-[57px] z-[5] -mx-4 border-y border-catch-beige-dark bg-catch-white/95 px-4 py-2.5 backdrop-blur sm:mx-0 sm:rounded-xl sm:border">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="inline-flex rounded-full bg-catch-grey p-0.5">
          {sorts.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => onSort(s.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                sort === s.key
                  ? 'bg-catch-white text-catch-black shadow-soft'
                  : 'text-gray-500 hover:text-catch-black'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className="hidden h-4 w-px bg-catch-beige-dark sm:block" />
        <FilterChip active={directOnly} onClick={onDirect} label="Direct only" />
        <FilterChip active={refundableOnly} onClick={onRefundable} label="Refundable" />
        <span className="ml-auto text-xs text-gray-400">
          {count} option{count === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  label
}: {
  active: boolean
  onClick: () => void
  label: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'border-catch-red bg-catch-red/10 text-catch-red'
          : 'border-catch-beige-dark text-gray-500 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  )
}

function Badge({ tone, children }: { tone: 'green' | 'amber' | 'red', children: React.ReactNode }): JSX.Element {
  const tones = {
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-catch-red/10 text-catch-red'
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  )
}

function FlightCard({
  option,
  maxTripMinutes,
  cheapestPrice,
  isCheapest,
  isFastest,
  isRecommended,
  selecting,
  disabled,
  onChoose
}: {
  option: FlightPageOption
  maxTripMinutes: number
  cheapestPrice: number
  isCheapest: boolean
  isFastest: boolean
  isRecommended: boolean
  selecting: boolean
  disabled: boolean
  onChoose: () => void
}): JSX.Element {
  const delta = Math.round(option.total_amount - cheapestPrice)
  return (
    <div
      className={`rounded-2xl border bg-white p-4 transition-shadow hover:shadow-soft sm:p-5 ${
        isRecommended ? 'border-catch-red/40 ring-1 ring-catch-red/20' : 'border-catch-beige-dark'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <AirlineLogo iata={option.airline_iata} name={option.airline} />
          <div className="min-w-0">
            <div className="truncate font-display text-base font-medium">
              {option.airline ?? 'Flight'}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              {isRecommended && <Badge tone="red">Recommended</Badge>}
              {isCheapest && <Badge tone="green">Cheapest</Badge>}
              {isFastest && <Badge tone="amber">Fastest</Badge>}
            </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="whitespace-nowrap font-sans text-lg font-semibold tabular-nums">
            {formatPrice(option.total_amount, option.currency)}
          </div>
          <div className="text-[11px] text-gray-500">
            {delta > 0
              ? `+${formatPrice(delta, option.currency)} vs cheapest`
              : 'total'}
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-2.5">
        {(option.slices ?? []).map((slice, idx) => (
          <SliceTimeline key={idx} slice={slice} maxTripMinutes={maxTripMinutes} />
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <span
          className={`text-xs font-medium ${
            option.refundable ? 'text-green-700' : 'text-gray-400'
          }`}
        >
          {option.refundable ? '✓ Refundable' : 'Non-refundable'}
          {option.cabin_class && (
            <span className="ml-2 capitalize text-gray-400">
              · {option.cabin_class.replace(/_/g, ' ')}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={onChoose}
          disabled={disabled}
          className="rounded-full bg-catch-red px-5 py-2 text-sm font-medium text-catch-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {selecting ? 'Choosing…' : 'Choose'}
        </button>
      </div>

      {option.rationale && (
        <p className="mt-2 border-t border-catch-beige-dark/70 pt-2 text-xs text-gray-500">
          {option.rationale}
        </p>
      )}
    </div>
  )
}

// A compact visual timeline: departure ●——(stops)——● arrival, with a
// duration bar scaled to the longest trip in the set so faster flights read as
// visibly shorter.
function SliceTimeline({
  slice,
  maxTripMinutes
}: {
  slice: FlightPageSlice
  maxTripMinutes: number
}): JSX.Element {
  const stops = sliceStops(slice)
  const mins = sliceMinutes(slice)
  const widthPct = Math.max(12, Math.round(((mins ?? 0) / maxTripMinutes) * 100))
  // Intermediate stop dots, evenly spaced between the endpoints.
  const stopDots = Array.from({ length: stops }, (_, i) => ((i + 1) / (stops + 1)) * 100)
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="w-12 shrink-0 text-sm font-semibold tabular-nums">
          {formatTime(slice.departing_at)}
        </span>
        <div className="relative flex-1" style={{ maxWidth: `${widthPct}%` }}>
          <div className="h-0.5 w-full rounded-full bg-catch-beige-dark" />
          <span className="absolute left-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-catch-red" />
          {stopDots.map((pos, i) => (
            <span
              key={i}
              className="absolute top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400"
              style={{ left: `${pos}%` }}
            />
          ))}
          <span className="absolute right-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-catch-red" />
        </div>
        <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums">
          {formatTime(slice.arriving_at)}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
        <span className="font-medium text-gray-600">
          {slice.origin ?? '?'} → {slice.destination ?? '?'}
        </span>
        <span>
          {formatMinutes(mins)} · {stopsLabel(slice)}
          <span className="ml-2 text-gray-400">{formatDate(slice.departing_at)}</span>
        </span>
      </div>
    </div>
  )
}
